<template>
  <div class="paycools-kyc-page">
    <section class="kyc-content">
      <!-- 标题区 -->
      <header class="kyc-header">
        <el-button type="text" style="padding:0;" @click="$emit('requestBack')">
          <svg-icon icon-class="back" class-name="kyc-back-icon" />
        </el-button>
        <h2 class="kyc-title">{{ $t('kyc.title') }}</h2>
      </header>

      <!-- 表单区 -->
      <el-form
        ref="signatoryForm"
        :model="form"
        :rules="signatoryRules"
        label-position="left"
        label-width="260px"
        class="kyc-form"
      >
        <div v-if="rejectionItems && rejectionItems.length" class="kyc-rejection-box" style="margin-bottom:16px;">
          <el-alert
            type="error"
            :title="$t('kyc.rejection_title')"
            :closable="false"
            show-icon
          >
            <div v-for="(item, index) in rejectionItems" :key="index" class="rejection-item">
              {{ index + 1 }}. {{ item }}
            </div>
          </el-alert>
        </div>
        <!-- Full Name -->
        <el-form-item prop="auth_signatory_name" :label="$t('kyc.signatory_info_legal_name_en')">
          <el-input
            v-model="form.auth_signatory_name"
            :placeholder="$t('kyc.placeholder_enter_name')"
            maxlength="100"
          />
        </el-form-item>

        <!-- Position -->
        <el-form-item prop="position_id" :label="$t('kyc.signatory_info_position')">
          <el-select v-model="form.position_id" :placeholder="$t('components.select')">
            <el-option
              v-for="(item, index) in jobList"
              :key="index"
              :label="language === 'zh' ? item.cn_name : item.en_name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>

        <!--        &lt;!&ndash; Email &ndash;&gt;-->
        <!--        <el-form-item prop="auth_signatory_email" :label="$t('components.email')">-->
        <!--          <el-input-->
        <!--            v-model="form.auth_signatory_email"-->
        <!--            :placeholder="$t('kyc.signatory_info_placeholder_enter_email')"-->
        <!--          />-->
        <!--        </el-form-item>-->

        <!-- Nationality -->
        <el-form-item prop="country_code" :label="$t('kyc.signatory_info_nationality')">
          <el-select v-model="form.country_code" :placeholder="$t('components.select')">
            <el-option
              v-for="item in nationalityOptions"
              :key="item.code"
              :label="language === 'zh' ? item.cn_name : item.en_name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>

        <!-- Type of document -->
        <el-form-item prop="id_type" :label="$t('kyc.signatory_info_id_type')">
          <el-select v-model="form.id_type" :placeholder="$t('components.select')">
            <el-option
              v-for="item in documentTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <!-- ID Number -->
        <el-form-item prop="id_number" :label="$t('kyc.signatory_info_id_number')">
          <el-input
            v-model="form.id_number"
            :placeholder="$t('kyc.placeholder_enter_id_number')"
            maxlength="100"
          />
        </el-form-item>

        <!-- Upload: ID Photo (Front) -->
        <el-form-item prop="id_face_photo" :label="$t('kyc.signatory_info_id_photo_front')">
          <UpdateFile
            ref="id_face_photo"
            v-model="form.id_face_photo"
            type="id_face_photo"
            :file-list="fileList"
            :uploading="uploading"
            :limit-num="1"
            :remove-files="removeFiles"
            :uploading-progress="uploadingProgress"
            @changeFileList="changeFileList"
            @changeUploading="changeUploading"
            @changeUploadingProgress="changeUploadingProgress"
            @changeRemoveFiles="changeRemoveFiles"
          />
        </el-form-item>

        <!-- Upload: ID Photo (Back) -->
        <el-form-item prop="id_back_photo" :label="$t('kyc.signatory_info_id_photo_back')">
          <UpdateFile
            ref="id_back_photo"
            v-model="form.id_back_photo"
            type="id_back_photo"
            :file-list="fileList"
            :uploading="uploading"
            :limit-num="1"
            :remove-files="removeFiles"
            :uploading-progress="uploadingProgress"
            @changeFileList="changeFileList"
            @changeUploading="changeUploading"
            @changeUploadingProgress="changeUploadingProgress"
            @changeRemoveFiles="changeRemoveFiles"
          />
        </el-form-item>

        <!-- Upload: Authorization Letter -->
        <el-form-item prop="authorization_letter" :label="$t('kyc.signatory_info_authorization_letter')">
          <UpdateFile
            ref="authorization_letter"
            v-model="form.authorization_letter"
            type="authorization_letter"
            :file-list="fileList"
            :uploading="uploading"
            :limit-num="1"
            :remove-files="removeFiles"
            :uploading-progress="uploadingProgress"
            @changeFileList="changeFileList"
            @changeUploading="changeUploading"
            @changeUploadingProgress="changeUploadingProgress"
            @changeRemoveFiles="changeRemoveFiles"
          />
        </el-form-item>

      </el-form>

      <!-- 底部按钮 -->
      <div class="kyc-footer">
        <el-button class="kyc-draft-btn" :disabled="isSubmitting" @click="handleSave">{{ $t('kyc.save_as_draft') }}</el-button>
        <el-button type="primary" class="kyc-next-btn" :disabled="isSubmitting" @click="handleNext">{{ $t('kyc.next_step') }}</el-button>
      </div>
    </section>
  </div>
</template>

<script>
import { getPositionOptions } from '@/maps/kyc'
import { updateSignatoryInfo } from '@/api/kyc/kyc'
import { getKycAllCountry } from '@/api/components/components'
import { personIDTypeMap } from '@/utils/maps'

const fileType = {
  id_face_photo: 38, // 证件照正面
  id_back_photo: 39, // 证件照反面
  authorization_letter: 9 // 授权书
}

export default {
  name: 'PaycoolsKycSignatoryInfo',
  components: {
    UpdateFile: () => import('@/views/paycools/kyc/UpdateFile'),
    SvgIcon: () => import('@/components/SvgIcon')
  },
  props: {
    kycData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    this.jobList = [{ en_name: 'President', cn_name: '总裁', code: 1 }, { en_name: 'Authorized Signatories', cn_name: '授权签字人', code: 2 }]
    return {
      form: {
        auth_signatory_name: '',
        // auth_signatory_email: '',
        position_id: '',
        country_code: '',
        id_type: '',
        id_number: '',
        id_face_photo: null,
        id_back_photo: null,
        authorization_letter: null
      },
      fileList: {
        id_face_photo: [], // 证件照正面
        id_back_photo: [], // 证件照反面
        authorization_letter: [] // 授权书
      },
      uploading: {
        id_face_photo: false,
        id_back_photo: false,
        authorization_letter: false
      },
      uploadingProgress: {
        id_face_photo: 0,
        id_back_photo: 0,
        authorization_letter: 0
      },
      removeFiles: [],
      countryList: [],
      localSubmitting: false,
      signatoryRules: {
        auth_signatory_name: [
          { required: true, message: this.$t('kyc.validation_full_name_required'), trigger: ['blur', 'change'] },
          { max: 100, message: this.$t('kyc.validation_length_100'), trigger: ['blur', 'change'] }
        ],
        // auth_signatory_email: [{ required: true, message: this.$t('kyc.validation_email_required'), validator: this.validateEmail, trigger: ['blur', 'change'] }],
        position_id: [{ required: true, message: this.$t('kyc.validation_position_required'), trigger: ['blur', 'change'] }],
        country_code: [{ required: true, message: this.$t('kyc.validation_nationality_required'), trigger: ['change'] }],
        id_type: [{ required: true, message: this.$t('kyc.validation_document_type_required'), trigger: ['change'] }],
        id_number: [
          { required: true, message: this.$t('kyc.validation_id_number_required'), trigger: ['blur', 'change'] },
          { max: 100, message: this.$t('kyc.validation_length_100'), trigger: ['blur', 'change'] },
          { pattern: /^[a-zA-Z0-9]+$/, message: this.$t('kyc.validation_id_number_format'), trigger: ['blur', 'change'] }
        ],
        id_face_photo: [{ required: true, message: this.$t('kyc.validation_idfront_required'), trigger: ['change'] }],
        id_back_photo: [{ required: true, message: this.$t('kyc.validation_idback_required'), trigger: ['change'] }],
        authorization_letter: [{ required: true, message: this.$t('kyc.validation_authorization_letter_required'), trigger: ['change'] }]
      }
    }
  },
  computed: {
    isSubmitting() {
      return this.submitting || this.localSubmitting
    },
    language() {
      return this.$store.getters.language
    },
    nationalityOptions() {
      return this.countryList
    },
    positionOptions() {
      return getPositionOptions()
    },
    documentTypeOptions() {
      return personIDTypeMap()
    },
    rejectionItems() {
      const audit = this.kycData && this.kycData.audit_info
      if (!audit || audit.kyc_status !== 4) return []
      return (audit.audit_back_list || []).map(item => item.back_content).filter(Boolean).slice(0, 3)
    }
  },
  watch: {
    kycData: {
      handler(newData) {
        if (newData) {
          const signerInfo = newData.signer_info || {}
          this.populateFormData(signerInfo)
        } else {
          // 如果没有数据，确保表单有默认值
          this.populateFormData({})
        }
      },
      immediate: true,
      deep: true
    },
    form: {
      deep: true,
      handler() {
        this.$emit('markChanged')
      }
    }
  },
  async created() {
    // init upload related state if not present
    if (!this.fileList) this.fileList = {
      id_face_photo: [],
      id_back_photo: [],
      authorization_letter: []
    }
    if (!this.uploading) this.uploading = {
      id_face_photo: false,
      id_back_photo: false,
      authorization_letter: false
    }
    if (!this.removeFiles) this.removeFiles = []
    if (!this.uploadingProgress) this.uploadingProgress = {
      id_face_photo: 0,
      id_back_photo: 0,
      authorization_letter: 0
    }

    // fetch country list
    try {
      const response = await getKycAllCountry()
      if (response.data && Array.isArray(response.data)) {
        this.countryList = response.data
      }
    } catch (error) {
      console.error('Failed to fetch country list:', error)
      this.$message.error('获取国家列表失败')
    }
  },
  methods: {
    validateEmail(rule, value, callback) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        return callback(new Error(this.$t('kyc.validation_email_invalid')))
      }
      return callback()
    },
    // file upload handlers (mirror other KYC pages) - unified behavior
    changeFileList(config) {
      // indexed types (keep array behavior)
      if (config.type && config.type.includes('___')) {
        let type = config.type
        const keyArr = type.split('___')
        const index = keyArr[0]
        type = keyArr[1]

        if (config.filter) {
          const imgList = this.form[index][type]
          const { uid } = config.file
          this.form[index][type] = imgList.filter(item => item.uid !== uid)
          return
        }

        if (config.push) {
          const { name, url } = config.file
          this.form[index][type].push({ name, url, content: config.resData, content_oss_url: config.content_oss_url })
          return
        }

        this.form[index][type] = config.typeFileList
        return
      }

      // non-indexed: use fileList + store URL in form
      if (config.push) {
        const { name, url, uid } = config.file
        // 检查是否已存在相同uid的文件，避免重复添加
        const existingIndex = this.fileList[config.type].findIndex(item => item.uid === uid)
        if (existingIndex !== -1) {
          return
        }
        this.fileList[config.type].push({
          name: name || (config.file && config.file.name),
          url: url,
          type: fileType[config.type],
          content: config.resData,
          content_oss_url: config.content_oss_url
        })
        this.$set(this.form, config.type, config.resData)
        // 使用 nextTick 确保表单状态更新后再清除验证
        this.$nextTick(() => {
          if (this.$refs['signatoryForm']) {
            this.$refs['signatoryForm'].clearValidate(config.type)
            // 额外触发一次验证，确保验证状态正确更新
            this.$refs['signatoryForm'].validateField(config.type, (errMsg) => {
              // 验证通过时不作处理，验证失败时会显示错误
            })
          }
        })
        return
      } else if (config.delete && config.typeFileList.length === 0) {
        this.$set(this.form, config.type, null)
        this.$refs['signatoryForm'] && this.$refs['signatoryForm'].validateField(config.type)
      }

      this.fileList[config.type] = config.typeFileList
    },
    changeUploading(config) {
      this.$set(this.uploading, config.type, config.value)
    },
    changeUploadingProgress(config) {
      this.$set(this.uploadingProgress, config.type, config.value)
    },
    changeRemoveFiles(config) {
      if (config.push) {
        const { uid } = config.file
        this.removeFiles.push(uid)
        return
      }
    },
    isRequired(type) {
      return ['id_photo_front', 'id_photo_back', 'authorization_letter'].includes(type)
    },
    async saveDraft() {
      return new Promise((resolve, reject) => {
        this.$refs['signatoryForm'].validate((valid, invalidateData) => {
          if (!valid) {
            // 表单验证失败，不在此处弹提示，由父组件统一处理
            reject(new Error('表单验证失败'))
            return
          }
          this.$emit('resetChanged')
          // 验证通过后执行异步保存逻辑
          this.performSave().then(resolve).catch(reject)
        })
      })
    },
    async performSave() {
      const formData = this.form || {}

      // separate fields for different processing
      const {
        country_code,
        authorization_letter,
        id_face_photo,
        id_back_photo,
        ...basicFormData
      } = formData

      // build signatory_files from fileList using fileType mapping (只包含授权书)
      const signatory_files = []
      if (this.fileList['authorization_letter']) {
        this.fileList['authorization_letter'].forEach(item => {
          const content = item.content
          const name = item.name || ''
          if (content) {
            signatory_files.push({ type: 9, content, name })
          }
        })
      }

      // build id_photo_files (证件照正面和反面)
      const id_photo_files = []
      ;['id_face_photo', 'id_back_photo'].forEach(key => {
        const files = this.fileList[key] || []
        const typeCode = fileType[key]
        files.forEach(item => {
          const content = item.content
          const name = item.name || ''
          if (content && typeCode) {
            id_photo_files.push({ type: typeCode, content, name })
          }
        })
      })

      const payload = {
        ...basicFormData,
        ...(country_code && { nationality: country_code }),
        signatory_files: signatory_files,
        id_photo_files: id_photo_files
      }

      // prune empty values from payload
      Object.keys(payload).forEach(key => {
        const val = payload[key]
        if (val === undefined || val === null || val === '') delete payload[key]
        // also prune empty arrays
        if (Array.isArray(val) && val.length === 0) delete payload[key]
      })

      const result = await updateSignatoryInfo(payload)
      this.$emit('saved')
      return result
    },
    async handleSave() {
      if (this.localSubmitting) return
      this.localSubmitting = true
      try {
        await this.saveDraft()
        this.$message.success(this.$t('kyc.save_draft_success'))
      } catch (error) {
        // 错误已在 saveDraft 中处理，这里不需要额外处理
      } finally {
        this.localSubmitting = false
      }
    },
    async handleNext() {
      if (this.localSubmitting) return
      this.localSubmitting = true
      try {
        await this.saveDraft()
        this.$emit('next')
      } catch (error) {
        // 错误已在 saveDraft 中处理，这里不需要额外处理
      } finally {
        this.localSubmitting = false
      }
    },
    populateFormData(data) {
      // 初始化 fileList，确保所有字段都有定义
      this.fileList = {
        id_face_photo: [], // 证件照正面
        id_back_photo: [], // 证件照反面
        authorization_letter: [] // 授权书
      }
      const safeData = data || {}

      // 将API数据填充到表单中
      this.form.auth_signatory_name = safeData.auth_signatory_name || ''
      this.form.position_id = safeData.position_id || ''
      this.form.country_code = safeData.nationality || '' // map nationality to country_code
      this.form.id_type = safeData.id_type || ''
      this.form.id_number = safeData.id_number || ''
      // this.form.auth_signatory_email = safeData.auth_signatory_email || ''
      this.form.date_of_birth = safeData.date_of_birth || ''

      // 处理文件上传 - 优先使用 OSS URL
      if (safeData.id_face_photo) {
        this.form.id_face_photo = safeData.id_face_photo
        this.fileList.id_face_photo = [{
          name: safeData.id_face_photo_file_name || 'id_front',
          url: safeData.id_face_photo_oss_url || safeData.id_face_photo,
          content: safeData.id_face_photo,
          content_oss_url: safeData.id_face_photo_oss_url || safeData.id_face_photo
        }]
      }

      if (safeData.id_back_photo) {
        this.form.id_back_photo = safeData.id_back_photo
        this.fileList.id_back_photo = [{
          name: safeData.id_back_photo_file_name || 'id_back',
          url: safeData.id_back_photo_oss_url || safeData.id_back_photo,
          content: safeData.id_back_photo,
          content_oss_url: safeData.id_back_photo_oss_url || safeData.id_back_photo
        }]
      }

      // 处理 signatory_files 数组，找到 type=9 的授权证书
      const companyFiles = data.signatory_files || []

      const typeMapping = {
        9: 'authorization_letter' // 授权书
      }

      // Process each company file
      companyFiles.forEach(file => {
        const formKey = typeMapping[file.type]
        if (formKey) {
          this.fileList[formKey].push({
            name: file.name || `${formKey}.pdf`,
            url: file.content_oss_url || file.content,
            content: file.content,
            content_oss_url: file.content_oss_url || file.content
          })
          this.$set(this.form, formKey, file.content)
          this.$refs['form'] && this.$refs['form'].clearValidate(formKey)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.paycools-kyc-page {
  display: flex;
  align-items: stretch;
  background-color: #f5f7fb;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
}

.kyc-sidebar {
  width: 240px;
  background-color: #f7f8fc;
  border-radius: 8px;
  padding: 24px 0;
  box-shadow: 0 0 0 1px #edf0f7;
}

.kyc-step-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.kyc-step-item {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  font-size: 14px;
  color: #7b8192;
  cursor: default;
}

.kyc-step-item.is-active {
  background-color: #ffffff;
  color: #2f3a60;
  font-weight: 600;
  border-left: 3px solid #3b82f6;
}

.kyc-step-index {
  margin-right: 8px;
}

.kyc-step-label {
  flex: 1;
}

.kyc-content {
  flex: 1;
  margin-left: 32px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 32px 40px 40px;
  box-shadow: 0 8px 20px rgba(15, 35, 95, 0.04);
  box-sizing: border-box;
}

.kyc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.kyc-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.kyc-subtitle {
  margin: 8px 0 20px;
  font-size: 14px;
  color: #6b7280;
}

.kyc-id-type {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #c7d2fe;
  max-width: 480px;
}

.kyc-id-type-btn {
  flex: 1;
  border: none;
  border-radius: 0;
  font-weight: 500;
}

.kyc-id-type-btn.is-active {
  background-color: #3b82f6;
  color: #ffffff;
}

.kyc-id-type-btn.is-ghost {
  background-color: #ffffff;
  color: #374151;
}

.kyc-form {
  max-width: 920px;
}

.kyc-inline-input-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.kyc-inline-date-group {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) auto minmax(0, 1.5fr);
  align-items: center;
  gap: 12px;
}

.kyc-inline-separator {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}

.kyc-upload-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.kyc-upload-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.kyc-upload-card {
  display: flex;
  flex-direction: column;
}

.kyc-upload-title {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.kyc-upload-box {
  border-radius: 8px;
  border: 1px dashed #d1d5db;
  background-color: #fafbff;
  padding: 20px 12px;
  text-align: center;
  color: #6b7280;
}

.kyc-upload-icon {
  font-size: 20px;
  color: #9ca3af;
  margin-bottom: 8px;
}

.kyc-upload-main-text {
  font-size: 14px;
  color: #2563eb;
  font-weight: 500;
  margin-bottom: 8px;
}

.kyc-upload-sub-text {
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 4px;
}

.kyc-upload-tip {
  font-size: 12px;
  color: #9ca3af;
}

.kyc-email-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kyc-email-add {
  padding-left: 0;
  padding-right: 0;
  color: #2563eb;
}

.kyc-footer {
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
  align-items: center;
}

.kyc-draft-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background-color: #ffffff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  border-radius: 6px;
}

.kyc-next-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
}

.rejection-item {
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}
</style>

